import { Method, executeRequest, getEndPointUrl } from "@utils";
import { IDSField, IQuery, IQueryBuilderContext, Param } from "./types";


export async function checkStatus(context: IQueryBuilderContext) {
    const endpoint = '/status'
    const result = await executeRequest(context, endpoint, Method.GET)
    if (result.error) {
        console.log('QUERY_ENGINE_IS_DOWN:', result.error)
        return { active: false, details: null }
    }
    return {
        active: (!!result.data && !!result.data?.msg),
        details: result.data?.details || null
    }
}

export interface CatalogDefinition {
    source: string;
    fields: IDSField[];
    description: string;
    error?: string | null;
    enabled?: boolean;
    params?: Param[]
}
export interface Catalog {
    definitions: CatalogDefinition[],
    relationships: any[]
}

export async function getAvailableSourcesForHistoricalData(context: IQueryBuilderContext): Promise<Catalog> {
    // const endpoint = '/v1/sources';
    const endpoint = '/v1/catalog/history';
    const result = await executeRequest(context, endpoint, Method.GET);
    if (result.error) {
        console.error('Error fetching available sources for historical data:', result.error);
        return { definitions: [], relationships: [] };
    }
    return result.data;
}

export async function getAvailableSources(context: IQueryBuilderContext): Promise<Catalog> {
    // const endpoint = '/v1/sources';
    const endpoint = '/v1/catalog';
    const result = await executeRequest(context, endpoint, Method.GET);
    if (result.error) {
        console.error('Error fetching available sources:', result.error);
        return { definitions: [], relationships: [] };
    }
    return result.data;
}

export async function executeQuery(context: IQueryBuilderContext, query: IQuery) {
    const endpoint = '/v1/execution';
    const method = Method.POST;
    const data = query
    return executeRequest(context, endpoint, method, { 'mode': 'preview' }, data);
}

const SchemaCache: { [schema: string]: { time: Date, data: any } } = {};
export async function getSchema(context: IQueryBuilderContext, query: IQuery) {
    let key = JSON.stringify(query);
    if (SchemaCache[key]) {
        let time = SchemaCache[key].time;
        if (Number(new Date()) - Number(time) < 1000 * 30) {
            console.log('Returning cached schema', query, SchemaCache);
            return SchemaCache[key].data;
        }
    }
    const endpoint = '/v1/schema';
    const method = Method.POST;
    const data = { ...query };
    let result = await executeRequest(context, endpoint, method, {}, data);
    if (result.error) {
        console.error('Error fetching schema:', result.error);
        return [];
    }
    SchemaCache[key] = { time: new Date(), data: result.data };
    return result.data;
}

export async function getCustomTransformation(context: IQueryBuilderContext, description: string) {
    const endpoint = '/v1/ai/custom-transform';
    const method = Method.POST;
    const data = { description };
    let result = await executeRequest(context, endpoint, method, {}, data);
    if (result.error) {
        throw new Error(result.error);
    }
    return result.data;
}
export async function applyCopilot(context: IQueryBuilderContext, query: IQuery, description: string) {
    const endpoint = '/v1/ai/copilot';
    const method = Method.POST;
    const data = { description };
    let result = await executeRequest(context, endpoint, method, { question: description }, query);
    if (result.error) {
        throw new Error(result.error);
    }
    return result.data;
}
export async function askQuestion(context: IQueryBuilderContext, question: string) {
    const endpoint = '/v1/ai/question';
    const method = Method.GET;
    let result = await executeRequest(context, endpoint, method, { question }, null);
    if (result.error) {
        throw new Error(result.error);
    }
    return result.data;
}

export async function getSampleQuestions(context: IQueryBuilderContext) {
    const endpoint = '/v1/ai/samples';
    const method = Method.GET;
    let result = await executeRequest(context, endpoint, method, {}, null);
    if (result.error) {
        console.error('Error fetching sample questions:', result.error);
        return [];
    }
    return result.data;
}

export async function resetConfig(context: IQueryBuilderContext) {
    const endpoint = '/config/reset';
    const method = Method.POST;
    let result = await executeRequest(context, endpoint, method, {}, null);
    if (result.error) {
        throw new Error(result.error);
    }
    return result.data;
}
export async function exportCsv(context: IQueryBuilderContext, query: IQuery) {
    try {
        const url = getEndPointUrl(context, '/v1/execution?format=csv');

        const response = await fetch(url, {
            method: 'POST', body: JSON.stringify(query), headers: {
                'Content-Type': 'application/json',
                'Authorization': `APIKEY ${context?.uxpContext.apiKey}`,
            }
        });

        if (!response.ok) {
            throw new Error('Network response was not ok.');
        }

        // Get the response body as a readable stream
        const reader = response.body.getReader();

        // Create a new readable stream to process the chunks
        const stream = new ReadableStream({
            start(controller) {
                // Define a function to process each chunk
                function push() {
                    // Read the next chunk
                    reader.read().then(({ done, value }) => {
                        console.log('STREAMING', value?.length, value);
                        if (done) {
                            // Close the stream when done
                            controller.close();
                            return;
                        }
                        // Enqueue the chunk into the stream
                        controller.enqueue(value);
                        // Process the next chunk
                        push();
                    }).catch(error => {
                        console.error('Stream error:', error);
                        controller.error(error);
                    });
                }
                // Start processing the stream
                push();
            }
        });

        // Convert the stream into a Blob
        const blob = await new Response(stream).blob();

        // Create a link element to trigger the download
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;

        // Set the filename for the download
        a.download = 'data.csv';

        // Append the link to the body
        document.body.appendChild(a);

        // Programmatically trigger a click on the link to start the download
        a.click();

        // Clean up by revoking the object URL and removing the link element
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);

    } catch (error) {
        console.error('There was a problem with the streaming download operation:', error);
        throw error;
    }
}



export async function exportQuery(context: IQueryBuilderContext, query: IQuery) {
    const endpoint = '/v1/execution?format=csv';
    const method = Method.POST;
    const data = query
    const result = await executeRequest(context, endpoint, method, { 'mode': 'preview' }, data);
    if (result.error) {
        throw new Error(result.error);
    }
    return result.data;
}

export async function getQueryDataSource(context: IQueryBuilderContext, queryId: string): Promise<{ source: CatalogDefinition }> {
    const endpoint = '/v1/catalog/query/' + queryId;
    const method = Method.GET;
    const result = await executeRequest(context, endpoint, method, {});
    if (result.error) {
        console.error('Unable to get query source. Error:', result.error)
        return null
    }
    return result.data;
}